/**
 * Live-editor text node inspector — root-cause investigation tool.
 *
 * Console-callable: `window.__inspectTextElement('el-id')` after this
 * module has been imported anywhere in the editor (it self-installs the
 * global on import).
 *
 * Returns AND console-logs an exhaustive snapshot covering every place a
 * text element's source/CSS/layout could differ from what's displayed:
 *
 *   - source text fields:  element.text, textContent, innerText
 *   - inline + computed text-affecting styles
 *   - getBoundingClientRect
 *   - Range rect for the whole content (line boxes)
 *   - per-word Range rects + gap measurements (proves spaces ARE rendered
 *     vs. NOT rendered)
 *   - DOM outerHTML
 *   - parent transform chain (zoom)
 *
 * Specifically designed to diagnose the "4 BATHROOMS → 4BATHROOMS" class
 * of bug — the per-word gap measurements answer the question definitively:
 *   gap > 0  → browser is rendering a space → bug is visual artifact
 *   gap == 0 → no space present in source → bug is in stored data
 */

function getDOMNodeForExportId(id) {
  if (!id || typeof document === 'undefined') return null;
  // Editor renders text elements with data-export-id (added in earlier rounds).
  return document.querySelector(`[data-export-id="${CSS.escape(id)}"]`);
}

function probePerWordRects(liveDiv) {
  if (!liveDiv) return null;
  const containerRect = liveDiv.getBoundingClientRect();
  const containerLeft = containerRect.left;
  const containerTop  = containerRect.top;

  const walker = document.createTreeWalker(liveDiv, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  const words = [];
  let node;
  while ((node = walker.nextNode())) {
    const data = node.data;
    if (!data) continue;
    const runs = data.match(/\S+|\s+/g);
    if (!runs) continue;
    let pos = 0;
    for (const run of runs) {
      const start = pos;
      const end = pos + run.length;
      pos = end;
      range.setStart(node, start);
      range.setEnd(node, end);
      const r = range.getBoundingClientRect();
      words.push({
        kind: /^\s+$/.test(run) ? 'whitespace' : 'word',
        text: JSON.stringify(run),
        x: +(r.left - containerLeft).toFixed(2),
        y: +(r.top  - containerTop).toFixed(2),
        w: +r.width.toFixed(2),
        h: +r.height.toFixed(2),
      });
    }
  }

  // Compute per-pair gaps between adjacent words on the same line.
  const gaps = [];
  for (let i = 0; i < words.length - 1; i++) {
    const a = words[i];
    const b = words[i + 1];
    if (Math.abs(a.y - b.y) > 1) continue;
    if (a.kind === 'whitespace' || b.kind === 'whitespace') continue;
    gaps.push({
      between: `${a.text} → ${b.text}`,
      gap: +(b.x - (a.x + a.w)).toFixed(2),
    });
  }
  return { words, gaps };
}

function buildSnapshot(liveEl) {
  if (!liveEl) return { error: 'no element' };
  const cs = window.getComputedStyle(liveEl);
  const rect = liveEl.getBoundingClientRect();

  // Whole-content line boxes from Range — one rect per visual line.
  const contentRange = document.createRange();
  contentRange.selectNodeContents(liveEl);
  const lineBoxes = Array.from(contentRange.getClientRects()).map((r) => ({
    x: +(r.left - rect.left).toFixed(2),
    y: +(r.top  - rect.top ).toFixed(2),
    w: +r.width.toFixed(2),
    h: +r.height.toFixed(2),
  }));

  // Walk up to canvas root, collecting transform chain (catches zoom).
  const transformChain = [];
  let n = liveEl;
  while (n && n.tagName !== 'BODY') {
    const ncs = window.getComputedStyle(n);
    const t = ncs.transform;
    if (t && t !== 'none') {
      transformChain.push({
        tag: n.tagName.toLowerCase(),
        id: n.id || null,
        cls: n.className || null,
        transform: t,
        origin: ncs.transformOrigin,
      });
    }
    n = n.parentElement;
  }

  return {
    id: liveEl.getAttribute('data-export-id'),
    text: {
      textContent:    liveEl.textContent,
      innerText:      liveEl.innerText,
      textContentJSON: JSON.stringify(liveEl.textContent),
      innerHTML:      liveEl.innerHTML.slice(0, 500),
      outerHTML:      liveEl.outerHTML.slice(0, 500),
    },
    geometry: {
      offsetWidth:  liveEl.offsetWidth,
      offsetHeight: liveEl.offsetHeight,
      clientWidth:  liveEl.clientWidth,
      clientHeight: liveEl.clientHeight,
      boundingRect: { x: rect.x, y: rect.y, w: rect.width, h: rect.height },
      naturalVisualScale: rect.width / (liveEl.offsetWidth || 1),
      lineBoxes,
    },
    style: {
      // Pinned: every property that could affect whitespace rendering.
      fontFamily:           cs.fontFamily,
      fontSize:             cs.fontSize,
      fontWeight:           cs.fontWeight,
      fontStyle:            cs.fontStyle,
      fontVariant:          cs.fontVariant,
      fontVariantLigatures: cs.fontVariantLigatures,
      fontFeatureSettings:  cs.fontFeatureSettings,
      fontKerning:          cs.fontKerning,
      fontSynthesis:        cs.fontSynthesis,
      fontVariationSettings: cs.fontVariationSettings,
      lineHeight:           cs.lineHeight,
      letterSpacing:        cs.letterSpacing,
      wordSpacing:          cs.wordSpacing,
      textRendering:        cs.textRendering,
      whiteSpace:           cs.whiteSpace,
      wordBreak:            cs.wordBreak,
      overflowWrap:         cs.overflowWrap,
      textTransform:        cs.textTransform,
      direction:            cs.direction,
      unicodeBidi:          cs.unicodeBidi,
      writingMode:          cs.writingMode,
      display:              cs.display,
      transform:            cs.transform,
      transformOrigin:      cs.transformOrigin,
    },
    transformChain,
    perWord: probePerWordRects(liveEl),
  };
}

export function inspectTextElement(id) {
  const el = getDOMNodeForExportId(id);
  if (!el) {
    // eslint-disable-next-line no-console
    console.warn(`[inspectTextElement] no element with data-export-id="${id}"`);
    return null;
  }
  const snap = buildSnapshot(el);
  // eslint-disable-next-line no-console
  console.log('[inspectTextElement]', id, snap);
  return snap;
}

export function inspectAllTextElements() {
  if (typeof document === 'undefined') return [];
  const all = document.querySelectorAll('[data-export-type="text"]');
  const out = [];
  all.forEach((el) => {
    const id = el.getAttribute('data-export-id');
    const snap = buildSnapshot(el);
    out.push({ id, snap });
  });
  // eslint-disable-next-line no-console
  console.log('[inspectAllTextElements] count=', out.length, out);
  return out;
}

// Self-install on import so the user can call directly from the console.
if (typeof window !== 'undefined') {
  window.__inspectTextElement = inspectTextElement;
  window.__inspectAllTextElements = inspectAllTextElements;
}
